import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, message } from "antd";
import { useQueryClient } from "react-query";

import { QUERY_KEY } from "@Constants/query-keys";
import {
  DOCTOR_ARRIVAL_POLL_MS,
  DOCTOR_ARRIVAL_RECHECK_MS,
  DOCTOR_ARRIVAL_REMINDER_MS,
  DOCTOR_ARRIVAL_STATUS_CHANGED_EVENT,
} from "@Constants/doctor-arrival-reminder";
import useDoctorArrived from "@Hooks/mutation/useDoctorArrived";
import useGetTodaysQueueInfo from "@Hooks/queries/useGetTodaysQueueInfo";
import { playChime, unlockChime } from "./chime";
import {
  checkReminder,
  clearReminderState,
  elapsedMinutesLabel,
  elapsedShiftMinutes,
  formatClinicClock,
  formatShiftTime,
  getReminderShift,
  reminderStorageKey,
  snoozeShiftReminder,
} from "./reminderSchedule";
import "./DoctorArrivalReminder.css";

export const TITLE_PREFIX = "(!) Cek kedatangan dokter \u2013 ";
export const ERROR_TEXT = "Gagal Mengubah Status Kedatangan Dokter";

// localStorage can throw (privacy mode, disabled storage): fall back to memory.
const createMemoryStorage = () => {
  const data = {};
  return {
    get length() {
      return Object.keys(data).length;
    },
    key: (i) => Object.keys(data)[i] || null,
    getItem: (k) => (Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null),
    setItem: (k, v) => {
      data[k] = String(v);
    },
    removeItem: (k) => {
      delete data[k];
    },
  };
};

const getBrowserStorage = () => {
  try {
    if (window.localStorage) return window.localStorage;
  } catch (e) {
    // ignore
  }
  return createMemoryStorage();
};

export const dispatchDoctorArrivalChanged = (detail) => {
  try {
    window.dispatchEvent(
      new CustomEvent(DOCTOR_ARRIVAL_STATUS_CHANGED_EVENT, { detail }),
    );
  } catch (e) {
    // very old browsers without CustomEvent constructor: polling still syncs
  }
};

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

/**
 * Reminds the admin every 10 minutes to tick "Status Kedatangan Dokter" while
 * the running Praktek shift is still unticked: a modal that must be answered,
 * plus an amber pill in the header between reminders. Mounted once in
 * AdminLayout, so it follows the admin to every admin page.
 */
const DoctorArrivalReminder = ({
  intervalMs = DOCTOR_ARRIVAL_REMINDER_MS,
  pollMs = DOCTOR_ARRIVAL_POLL_MS,
  recheckMs = DOCTOR_ARRIVAL_RECHECK_MS,
}) => {
  const queryClient = useQueryClient();
  const todaysQueueInfo = useGetTodaysQueueInfo({ refetchInterval: pollMs });
  const mutationDoctorArrived = useDoctorArrived();

  const storageRef = useRef(null);
  if (!storageRef.current) storageRef.current = getBrowserStorage();
  const storage = storageRef.current;

  const [now, setNow] = useState(() => Date.now());
  const [schedule, setSchedule] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [step, setStep] = useState("ask");
  const [errorText, setErrorText] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Request error / no running shift / missing shift id -> no reminder at all.
  const shift = todaysQueueInfo.isError
    ? null
    : getReminderShift(todaysQueueInfo.data, now);
  const pendingShift = shift && !shift.arrived ? shift : null;
  const pendingKey = pendingShift
    ? reminderStorageKey(pendingShift.id, pendingShift.date)
    : null;
  const arrivedKey =
    shift && shift.arrived ? reminderStorageKey(shift.id, shift.date) : null;

  const visible = !!pendingShift && (manualOpen || !!(schedule && schedule.open));

  // Latest values for timer / event callbacks.
  const pendingShiftRef = useRef(pendingShift);
  pendingShiftRef.current = pendingShift;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const dataUpdatedAtRef = useRef(todaysQueueInfo.dataUpdatedAt);
  dataUpdatedAtRef.current = todaysQueueInfo.dataUpdatedAt;
  const submittingRef = useRef(false);
  // Another tab ticked it: hold scheduling until the server answers again
  // (dataUpdatedAt changes), so this tab's stale "pending" data can't re-fire.
  const holdWhileDataUpdatedAtRef = useRef(null);
  // Keeps the modal content while it animates closed.
  const lastShiftRef = useRef(pendingShift);
  if (pendingShift) lastShiftRef.current = pendingShift;

  const refreshStatus = useCallback(
    () => queryClient.invalidateQueries([QUERY_KEY.GET_TODAYS_QUEUE_INFO]),
    [queryClient],
  );

  const runCheck = useCallback(() => {
    const current = Date.now();
    setNow(current);
    const target = pendingShiftRef.current;
    if (!target || submittingRef.current) return;
    if (holdWhileDataUpdatedAtRef.current !== null) {
      if (dataUpdatedAtRef.current === holdWhileDataUpdatedAtRef.current) return;
      holdWhileDataUpdatedAtRef.current = null;
    }

    const result = checkReminder({
      storage,
      shift: target,
      now: current,
      intervalMs,
    });
    setSchedule(result.state);
    if (result.fired) {
      if (!visibleRef.current) {
        setStep("ask");
        setErrorText(null);
      }
      playChime();
    }
  }, [storage, intervalMs]);

  // New pending shift (shift start, new day, untick) or no longer pending:
  // reset the dialog and schedule straight away.
  useEffect(() => {
    setManualOpen(false);
    setStep("ask");
    setErrorText(null);
    if (!pendingKey) {
      setSchedule(null);
      return;
    }
    runCheck();
  }, [pendingKey, runCheck]);

  // Ticked (here, on the Antrian Klinik page or on another PC): stop and
  // forget the schedule, so an untick later starts fresh.
  useEffect(() => {
    if (arrivedKey) clearReminderState(storage, arrivedKey);
  }, [arrivedKey, storage]);

  // Timers are throttled in background tabs: re-check on an interval and
  // whenever the tab becomes visible / focused again.
  useEffect(() => {
    const timer = setInterval(runCheck, recheckMs);
    const onVisible = () => {
      if (!document.hidden) runCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", runCheck);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", runCheck);
    };
  }, [runCheck, recheckMs]);

  // Sync with the Antrian Klinik checkbox and with other tabs of this browser.
  useEffect(() => {
    const onStatusChanged = (event) => {
      if (event && event.detail && event.detail.source === "reminder") return;
      refreshStatus();
    };
    const onStorage = (event) => {
      const target = pendingShiftRef.current;
      if (!target) return;
      if (event.key !== reminderStorageKey(target.id, target.date)) return;
      if (event.newValue === null) {
        // Another tab ticked it (or saw it ticked): confirm with the server.
        holdWhileDataUpdatedAtRef.current = dataUpdatedAtRef.current;
        refreshStatus();
      } else {
        runCheck();
      }
    };
    window.addEventListener(DOCTOR_ARRIVAL_STATUS_CHANGED_EVENT, onStatusChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        DOCTOR_ARRIVAL_STATUS_CHANGED_EVENT,
        onStatusChanged,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [refreshStatus, runCheck]);

  // Browsers keep audio suspended until a user gesture; unlock on the first one.
  useEffect(() => {
    window.addEventListener("pointerdown", unlockChime, true);
    window.addEventListener("keydown", unlockChime, true);
    return () => {
      window.removeEventListener("pointerdown", unlockChime, true);
      window.removeEventListener("keydown", unlockChime, true);
    };
  }, []);

  // Tab title while a reminder is outstanding.
  const alerting = !!pendingShift && !!schedule && schedule.count > 0;
  useEffect(() => {
    if (!alerting) return undefined;
    const original = document.title;
    const alerted = `${TITLE_PREFIX}${original}`;
    document.title = alerted;
    return () => {
      if (document.title === alerted) document.title = original;
    };
  }, [alerting]);

  const onOpenFromPill = () => {
    setManualOpen(true);
    setStep("ask");
    setErrorText(null);
  };

  const onSnooze = () => {
    const target = pendingShiftRef.current;
    if (!target) return;
    const state = snoozeShiftReminder({
      storage,
      shift: target,
      now: Date.now(),
      intervalMs,
    });
    setSchedule(state);
    setManualOpen(false);
    setStep("ask");
    setErrorText(null);
  };

  const onConfirm = () => {
    const target = pendingShiftRef.current;
    if (!target || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setErrorText(null);

    // Same request as the Antrian Klinik checkbox (Queue.jsx, useDoctorArrived):
    // POST admin/shifts/{id} { _method: PATCH, doctor_arrival_status: 1 }.
    mutationDoctorArrived.mutate(
      { _method: "PATCH", doctor_arrival_status: 1, shiftId: target.id },
      {
        onSuccess: () => {
          clearReminderState(storage, reminderStorageKey(target.id, target.date));
          queryClient.setQueryData([QUERY_KEY.GET_TODAYS_QUEUE_INFO], (old) =>
            old && old.shift && old.shift.id === target.id
              ? { ...old, shift: { ...old.shift, doctor_arrival_status: 1 } }
              : old,
          );
          setSchedule(null);
          setManualOpen(false);
          setStep("ask");
          message.success(`Dokter ditandai sudah di klinik \u00b7 Shift ${target.name}`);
          refreshStatus();
          dispatchDoctorArrivalChanged({
            shiftId: target.id,
            doctor_arrival_status: 1,
            source: "reminder",
          });
        },
        onError: () => {
          setErrorText(ERROR_TEXT);
        },
        onSettled: () => {
          submittingRef.current = false;
          setSubmitting(false);
        },
      },
    );
  };

  const displayShift = pendingShift || lastShiftRef.current;
  const count = schedule ? schedule.count : 0;
  const intervalMinutes = Math.round(intervalMs / 60000);

  const renderDialog = () => {
    if (!displayShift) return null;
    const start = formatShiftTime(displayShift.startTime);
    const end = formatShiftTime(displayShift.endTime);
    const range = start && end ? ` (${start} \u2013 ${end})` : "";
    const meta = `Shift ${displayShift.name}${range} \u00b7 ${formatClinicClock(now)}`;
    const elapsed = elapsedMinutesLabel(
      elapsedShiftMinutes(displayShift.startTime, displayShift.date, now),
    );
    const isConfirm = step === "confirm";

    return (
      <div className="dar-dialog" data-testid="doctor-arrival-reminder-dialog">
        <div className="dar-head">
          <div className="dar-icon">
            <BellIcon />
          </div>
          <div className="dar-head-text">
            <div className="dar-title" id="dar-title">
              {isConfirm ? "Konfirmasi kedatangan dokter" : "Cek kedatangan dokter"}
            </div>
            <div className="dar-meta">{meta}</div>
          </div>
        </div>

        {isConfirm ? (
          <>
            <div className="dar-body">
              <b>Tandai dokter sudah di klinik untuk Shift {displayShift.name}?</b>{" "}
              Pastikan dokter benar-benar sudah datang. Setelah ditandai:
            </div>
            <ul className="dar-effects">
              <li>Layar antrian TV berubah menjadi “Dokter Sudah di Klinik”.</li>
              <li>
                Orang tua pasien di shift ini menerima notifikasi aplikasi dan
                WhatsApp.
              </li>
              <li>Jam kedatangan yang disarankan mulai dihitung untuk setiap antrian.</li>
            </ul>
            {errorText ? (
              <div className="dar-error" role="alert">
                {errorText}
              </div>
            ) : null}
            <div className="dar-actions">
              <Button
                className="dar-btn"
                disabled={submitting}
                onClick={() => {
                  setStep("ask");
                  setErrorText(null);
                }}
              >
                Kembali
              </Button>
              <Button
                className="dar-btn dar-btn-ok"
                loading={submitting}
                disabled={submitting}
                onClick={onConfirm}
              >
                Ya, tandai sudah di klinik
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="dar-body">
              Shift <b>{displayShift.name}</b>{" "}
              {elapsed ? (
                <>
                  sudah berjalan <b>{elapsed}</b>
                </>
              ) : (
                "baru saja dimulai"
              )}
              , tapi status kedatangan dokter <b>belum dicentang</b>.
              {count > 1 ? (
                <span className="dar-count">Pengingat ke-{count}</span>
              ) : null}
              <br />
              Apakah dokter sudah ada di klinik?
            </div>
            <div className="dar-actions">
              <Button className="dar-btn" onClick={onSnooze}>
                Belum, ingatkan {intervalMinutes} menit lagi
              </Button>
              <Button
                className="dar-btn dar-btn-ok"
                onClick={() => {
                  setStep("confirm");
                  setErrorText(null);
                }}
              >
                Ya, dokter sudah di klinik
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <span className="doctor-arrival-reminder">
      {pendingShift ? (
        <button
          type="button"
          className="dar-pill"
          title="Buka pengingat"
          onClick={onOpenFromPill}
          data-testid="doctor-arrival-reminder-pill"
        >
          <span className="dar-pill-dot" aria-hidden="true" />
          <span>Kedatangan dokter belum dicentang · Shift {pendingShift.name}</span>
        </button>
      ) : null}
      <Modal
        open={visible}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        title={null}
        centered
        width={560}
        wrapClassName="dar-modal-wrap"
        destroyOnClose
      >
        {renderDialog()}
      </Modal>
    </span>
  );
};

export default DoctorArrivalReminder;
